import React, { useState, useCallback, useMemo, useEffect } from "react";
import "./RpcTab.styles.scss";

// Component Imports
import { RpcForm } from "../RpcForm/RpcForm";
import { UseEndpointProvider } from "../../contexts";

// Hook Imports
import { useSchemaProcessor } from "@/hooks";
import { useAlerts } from "@/hooks";
import { useSubmitRpcMutation } from "../../services/algoManagementApi";

// Type Imports
import { IInteractionRpc } from "@pulse-ui/codegen/generated_ts/managed_algo";
import { toPlainSchemaValue, computeDefaultFormDataFromBot } from "../../utils";

// Package Imports
import { v4 as uuidv4 } from "uuid";

export interface RpcTabProps {
  rpcName: string;
  rpcSchema: any;
  interaction: IInteractionRpc;
  value: number;
  index: number;
  formData: any;
  selectedBotData?: any;
  onFormDataChange: (data: any) => void;
  onSubmitConfig?: RpcSubmitConfig;
}

export interface RpcSubmitConfig {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  closeModal?: boolean;
  showSuccess?: boolean;
  showError?: boolean;
  resetFormOnSuccess?: boolean;
}

/**
 * Tab panel component that wraps an RPC form
 * Only renders form when tab is active (lazy loading)
 *
 * @example
 * <RpcTab
 *   rpcName="submit_simple_spread"
 *   rpcSchema={rpcSchema}
 *   interaction={interaction}
 *   value={activeTab}
 *   index={0}
 *   formData={formData}
 *   selectedBotData={selectedBotData}
 *   onFormDataChange={handleChange}
 *   onSubmitConfig={{
 *     showSuccess: true,
 *     resetFormOnSuccess: false,
 *   }}
 * />
 */
export const RpcTab = ({
  rpcName,
  rpcSchema,
  interaction,
  value,
  index,
  formData,
  selectedBotData,
  onFormDataChange,
  onSubmitConfig = {},
}: RpcTabProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [filteredValidationErrors, setFilteredValidationErrors] = useState<any[]>([]);
  const [manualValidationErrors, setManualValidationErrors] = useState<string[]>([]);
  const { showError, showSuccess } = useAlerts();
  const [submitRpc] = useSubmitRpcMutation();

  // Wrapped validation change handler
  const handleValidationChange = useCallback((errors: any[]) => {
    setValidationErrors(errors);
  }, []);

  // Only render form when tab is active (lazy loading)
  const isActive = value === index;

  // Check if schema is ready (normalize components so $ref "#/components/strategy/0" etc. resolve)
  const schemaWithComponents = useMemo(() => {
    if (!rpcSchema) return null;
    const components = (interaction as any).components;
    let plainComponents: Record<string, unknown>;
    if (components != null && typeof components === "object") {
      plainComponents = toPlainSchemaValue(components) as Record<
        string,
        unknown
      >;
      // Ensure strategy is array-like so #/components/strategy/0 and /1 resolve (dereference uses bracket access)
      const strategy = plainComponents.strategy;
      if (
        strategy != null &&
        typeof strategy === "object" &&
        !Array.isArray(strategy)
      ) {
        const keys = Object.keys(strategy)
          .filter((k) => /^\d+$/.test(k))
          .sort((a, b) => Number(a) - Number(b));
        if (keys.length > 0) {
          plainComponents.strategy = keys.map(
            (k) => (strategy as Record<string, unknown>)[k],
          );
        }
      }
      if (!Array.isArray(plainComponents.strategy)) {
        plainComponents.strategy = [];
      }
    } else {
      plainComponents = { strategy: [] };
    }
    // Always set components so refs like #/components/strategy/0 don't hit null (backend may send components: null)
    return { ...rpcSchema, components: plainComponents };
  }, [rpcSchema, interaction]);

  const { processedSchema, isLoading: isProcessingSchema } = useSchemaProcessor(
    isActive ? schemaWithComponents : null,
  );

  // Compute initial form data from the fully-resolved processedSchema (not the raw rpcSchema which
  // may still contain $ref pointers). This ensures propSchema.properties is available for nested
  // fields like config.fields when determining how to map selectedBotData values.
  const initialFormData = useMemo((): Record<string, unknown> | null => {
    if (!selectedBotData || !processedSchema) return null;
    return computeDefaultFormDataFromBot(processedSchema, selectedBotData);
  }, [processedSchema, selectedBotData]);

  const effectiveFormData = useMemo(() => {
    // Merge initialFormData with formData, giving precedence to formData for edited fields.
    // Do not allow empty objects from JsonForms initialization to overwrite populated initial values.
    if (!initialFormData || Object.keys(initialFormData).length === 0) {
      return formData ?? {};
    }
    if (!formData || Object.keys(formData).length === 0) {
      return initialFormData;
    }
    // Both exist: merge with formData taking precedence, but skip empty-object overrides
    const result: Record<string, unknown> = { ...initialFormData };
    for (const [key, val] of Object.entries(formData)) {
      const isEmptyObj =
        val !== null &&
        typeof val === "object" &&
        !Array.isArray(val) &&
        Object.keys(val as object).length === 0;
      const initVal = initialFormData[key];
      const initIsPopulated =
        initVal !== null &&
        typeof initVal === "object" &&
        !Array.isArray(initVal) &&
        Object.keys(initVal as object).length > 0;
      if (isEmptyObj && initIsPopulated) {
        // Keep the pre-populated initial value rather than overwriting with {}
      } else {
        result[key] = val;
      }
    }
    return result;
  }, [formData, initialFormData]);

  // Helper function to validate required fields (including nested objects)
  const validateRequiredFields = useCallback(
    (data: any, schema: any): string[] => {
      const missingFields: string[] = [];

      if (!schema?.properties) {
        return missingFields;
      }

      const checkRequired = (
        obj: any,
        properties: any,
        required: string[] | undefined,
        path = "",
      ) => {
        if (!required || required.length === 0) {
          return;
        }

        required.forEach((fieldName: string) => {
          const fieldPath = path ? `${path}.${fieldName}` : fieldName;
          const fieldSchema = properties[fieldName];
          if (!fieldSchema) return;

          const fieldValue = obj?.[fieldName];

          // Check if field is missing or empty
          if (
            fieldValue === undefined ||
            fieldValue === null ||
            fieldValue === ""
          ) {
            // Get field title for better error message
            const fieldTitle = fieldSchema?.title || fieldName;
            missingFields.push(fieldTitle);
          } else if (
            fieldSchema?.type === "object" &&
            fieldSchema?.properties
          ) {
            // Recursively check nested required fields
            const nestedRequired = fieldSchema?.required || [];
            if (nestedRequired.length > 0) {
              checkRequired(
                fieldValue,
                fieldSchema.properties,
                nestedRequired,
                fieldPath,
              );
            }
          }
        });
      };

      // Check all object properties for nested required fields
      const checkNestedObjects = (obj: any, properties: any, path = "", parentRequired: string[] = []) => {
        Object.keys(properties).forEach((fieldName: string) => {
          const fieldSchema = properties[fieldName];

          // If it's an object schema with nested required fields, validate them
          if (fieldSchema?.type === "object" && fieldSchema?.properties) {
            const nestedRequired = fieldSchema?.required || [];
            if (nestedRequired.length > 0) {
              const fieldPath = path ? `${path}.${fieldName}` : fieldName;
              const fieldValue = obj?.[fieldName];

              // Check if this field is required at the current level
              const isParentRequired = parentRequired.includes(fieldName);

              // If the field is not provided (undefined/null) and the parent is optional, skip validation
              // This prevents validating nested required fields for optional parent objects that aren't present
              if (!fieldValue && !isParentRequired) {
                return; // Skip validation for optional fields that don't exist
              }

              // Use actual value if it exists and is an object, otherwise use empty object
              // Only for required parents or when the parent object exists but may be incomplete
              const nestedObj =
                fieldValue &&
                typeof fieldValue === "object" &&
                !Array.isArray(fieldValue)
                  ? fieldValue
                  : {};

              // Validate the nested object's required fields
              checkRequired(
                nestedObj,
                fieldSchema.properties,
                nestedRequired,
                fieldPath,
              );
            }
          }
        });
      };

      const dataObj = data || {};
      const topLevelRequired = schema?.required || [];

      // Check top-level required fields
      checkRequired(dataObj, schema.properties, topLevelRequired);

      // Also check all nested objects for required fields (handles cases like exchange.platform)
      // This checks even if the parent object exists but is incomplete
      // IMPORTANT: This must check ALL object properties in the schema, not just those in the data
      // This ensures both "exchange" and "hedger_exchange" are validated consistently
      checkNestedObjects(dataObj, schema.properties, "", topLevelRequired);

      return missingFields;
    },
    [],
  );

  // Run manual validation in real-time after first submit attempt
  useEffect(() => {
    if (hasAttemptedSubmit && processedSchema && effectiveFormData) {
      const missingFields = validateRequiredFields(effectiveFormData, processedSchema);
      setManualValidationErrors(missingFields);
    }
  }, [hasAttemptedSubmit, effectiveFormData, processedSchema, validateRequiredFields, rpcName]);

  // Filter JSON Forms validation errors so we don't block submission on
  // required fields from inactive oneOf branches (e.g. ExposureConfig when Polaris is selected).
  const filterValidationErrorsForActiveRawConfig = useCallback(
    (errors: any[], data: any, schema: any): any[] => {
      if (
        !Array.isArray(errors) ||
        !schema?.properties?.raw_config ||
        !data?.raw_config
      ) {
        return errors;
      }

      const activeStrategyType = data.raw_config.strategy_type;
      const rawConfigSchema = schema.properties.raw_config;
      const oneOf = Array.isArray(rawConfigSchema.oneOf)
        ? rawConfigSchema.oneOf
        : null;
      if (!oneOf || oneOf.length === 0) {
        return errors;
      }

      // When strategy_type is missing, use first branch so we only show that branch's required errors (e.g. Polaris), not the other (e.g. ExposureConfig).
      const activeBranch =
        activeStrategyType != null
          ? oneOf.find(
              (branch: any) =>
                branch?.properties?.strategy_type?.const === activeStrategyType,
            )
          : oneOf[0];

      const activeIndex = activeBranch ? oneOf.indexOf(activeBranch) : -1;
      if (!activeBranch || activeIndex === -1 || !activeBranch.properties) {
        return errors;
      }

      const filtered = errors.filter((error) => {
        if (!error || typeof error !== "object") return true;

        // Drop generic oneOf error from list so we don't show "must match exactly one schema in oneOf";
        // required/type errors from the active branch are enough.
        if (error.keyword === "oneOf") return false;

        const schemaPath: string | undefined = error.schemaPath;
        if (typeof schemaPath === "string") {
          // If the error originates from a non-active oneOf branch (e.g. Exposure when Polaris is selected),
          // drop it entirely.
          const m = schemaPath.match(
            /#\/properties\/raw_config\/oneOf\/(\d+)\//,
          );
          if (m) {
            const branchIndex = Number(m[1]);
            if (!Number.isNaN(branchIndex) && branchIndex !== activeIndex) {
              return false;
            }
          }
        }

        if (error.keyword !== "required") return true;

        const instancePath =
          error.instancePath || error.schemaPath || error.path || "";
        if (
          typeof instancePath !== "string" ||
          !instancePath.startsWith("/raw_config")
        ) {
          return true;
        }

        const missing =
          (error.params && error.params.missingProperty) ||
          (typeof error.message === "string"
            ? (error.message.match(/required property ['"]([^'"]+)['"]/) ||
                [])[1]
            : undefined);

        if (!missing) {
          return true;
        }

        // Keep the error only if the missing property exists on the active raw_config schema.
        return Boolean(activeBranch.properties[missing]);
      });
      return filtered;
    },
    [],
  );

  // Helper to generate a simple trade_id when missing. This is a best-effort
  // client-side fallback; the backend still validates according to its own rules.
  const generateTradeId = useCallback((tradeName?: unknown): string => {
    const length = 8;
    if (typeof tradeName === "string" && tradeName.trim().length > 0) {
      const slug = tradeName.toLowerCase().replace(/[^a-f0-9]/g, "");
      if (slug.length >= length) {
        return slug.slice(0, length);
      }
    }
    const chars = "abcdef0123456789";
    let out = "";
    for (let i = 0; i < length; i += 1) {
      out += chars[Math.floor(Math.random() * chars.length)];
    }
    return out;
  }, []);

  // Memoize submit handler to prevent unnecessary re-renders
  const handleSubmit = useCallback(
    async (data: any) => {
      // Mark that user has attempted to submit (show errors from now on)
      setHasAttemptedSubmit(true);

      // Normalize form data before validation / submission (fixes nested shapes from OptionalFieldsAdder, etc.)
      let normalizedData = data ?? {};

      // Strip discriminators we injected for anyOf→oneOf (e.g. signal_parameters_type) so backend receives original shape
      const anyOfTransformed = (processedSchema as any)?.__anyOfTransformed as
        | string[]
        | undefined;
      if (Array.isArray(anyOfTransformed) && anyOfTransformed.length > 0) {
        normalizedData = { ...normalizedData };
        anyOfTransformed.forEach((propKey) => {
          const obj = normalizedData[propKey];
          const discKey = `${propKey}_type`;
          if (
            obj &&
            typeof obj === "object" &&
            !Array.isArray(obj) &&
            discKey in obj
          ) {
            normalizedData = {
              ...normalizedData,
              [propKey]: (() => {
                const { [discKey]: _strip, ...rest } = obj as Record<
                  string,
                  unknown
                >;
                return rest;
              })(),
            };
          }
        });
      }

      // Helper to recursively transform additionalProperties arrays back to objects
      const transformArraysToObjects = (dataObj: any, schemaObj: any): any => {
        if (
          !dataObj ||
          typeof dataObj !== "object" ||
          !schemaObj ||
          typeof schemaObj !== "object"
        ) {
          return dataObj;
        }

        let result = { ...dataObj };

        // Handle oneOf: recursively process each branch
        if (Array.isArray(schemaObj.oneOf)) {
          schemaObj.oneOf.forEach((branch: any) => {
            result = transformArraysToObjects(result, branch);
          });
        }

        // Handle properties
        if (schemaObj.properties && typeof schemaObj.properties === "object") {
          Object.keys(schemaObj.properties).forEach((key) => {
            const propSchema = schemaObj.properties[key];

            // Transform this property if it's from additionalProperties
            if (
              propSchema?.__fromAdditionalProperties &&
              Array.isArray(result[key])
            ) {
              const arrayData = result[key] as any[];
              const objectData: Record<string, any> = {};

              arrayData.forEach((item: any) => {
                if (item && typeof item === "object" && "__key" in item) {
                  const { __key, ...rest } = item;
                  if (typeof __key === "string" && __key.trim()) {
                    objectData[__key] = rest;
                  }
                }
              });

              result[key] = objectData;
            }
            // Recursively process nested objects
            else if (
              result[key] &&
              typeof result[key] === "object" &&
              !Array.isArray(result[key])
            ) {
              result[key] = transformArraysToObjects(result[key], propSchema);
            }
          });
        }

        return result;
      };

      // Transform additionalProperties fields back from array to object format recursively
      // E.g. skew: [{ __key: "type1", ... }] → skew: { "type1": { ... } }
      if (processedSchema) {
        normalizedData = transformArraysToObjects(
          normalizedData,
          processedSchema,
        );
      }

      if (
        normalizedData.raw_config &&
        typeof normalizedData.raw_config === "object"
      ) {
        const rc = normalizedData.raw_config as any;
        const t = rc.trade_id;
        // Some paths (e.g. optional field adder) can leave trade_id shaped as { trade_id: "value" }.
        // The schema expects a string/null, so flatten that case.
        if (
          t &&
          typeof t === "object" &&
          "trade_id" in t &&
          typeof t.trade_id === "string"
        ) {
          normalizedData = {
            ...normalizedData,
            raw_config: {
              ...rc,
              trade_id: t.trade_id,
            },
          };
          onFormDataChange(normalizedData);
        } else if (t === undefined || t === null || t === "") {
          // Only auto-generate when the active strategy's schema defines trade_id (e.g. Polaris/Arber),
          // not for strategies that don't have it (e.g. spreader).
          const rawConfigSchema = processedSchema?.properties?.raw_config;
          const oneOf = Array.isArray(rawConfigSchema?.oneOf)
            ? rawConfigSchema.oneOf
            : null;
          const activeStrategyType = rc.strategy_type;
          const activeBranch =
            oneOf && activeStrategyType
              ? oneOf.find(
                  (b: any) =>
                    b?.properties?.strategy_type?.const === activeStrategyType,
                )
              : null;
          const branchHasTradeId =
            activeBranch?.properties &&
            typeof activeBranch.properties === "object" &&
            "trade_id" in activeBranch.properties;

          if (branchHasTradeId) {
            const autoId = generateTradeId(rc.trade_name);
            normalizedData = {
              ...normalizedData,
              raw_config: {
                ...rc,
                trade_id: autoId,
              },
            };
            onFormDataChange(normalizedData);
          }
        }

        // Optional field adder can leave some fields wrapped as objects, e.g.
        // quoting_account: { quoting_account: "value" } or hedging_markets:
        // { hedging_markets: [...] }. Flatten these so validators see the
        // expected scalar/array shapes.
        const flattenOptionalField = (object: any, key: string) => {
          const value = object?.[key];
          if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            key in value
          ) {
            object[key] = value[key];
          }
        };

        const nextRc = { ...rc };
        flattenOptionalField(nextRc, "quoting_account");
        flattenOptionalField(nextRc, "hedging_markets");
        flattenOptionalField(nextRc, "hedging_accounts");

        if (nextRc !== rc) {
          normalizedData = {
            ...normalizedData,
            raw_config: nextRc,
          };
          onFormDataChange(normalizedData);
        }
      }

      // Always run manual validation as primary check (more reliable for nested fields)
      let missingFields: string[] = [];
      if (processedSchema) {
        missingFields = validateRequiredFields(normalizedData, processedSchema);
      }

      // Store manual validation errors in state so error panel can display them
      setManualValidationErrors(missingFields);

      // Also check JSON Forms validation errors, filtering out errors from inactive oneOf branches.
      // IMPORTANT: we only treat *required* keyword errors as blockers here. Type/enum/const
      // errors for optional fields can be stale (e.g. when OptionalFieldsAdder temporarily
      // wraps values) and we rely on backend validation for those.
      const effectiveErrors =
        processedSchema && Array.isArray(validationErrors)
          ? filterValidationErrorsForActiveRawConfig(
              validationErrors,
              normalizedData,
              processedSchema,
            )
          : validationErrors;

      // Helper to check if a parent path is optional and empty/has no meaningful values
      const isOptionalEmptyParent = (instancePath: string, data: any, schema: any): boolean => {
        if (!instancePath) return false;

        const pathParts = instancePath.split('/').filter(Boolean);
        if (pathParts.length === 0) return false;

        // Navigate to parent object in data
        let current = data;
        let currentSchema = schema;
        for (const part of pathParts) {
          if (!current || typeof current !== 'object') return false;
          current = current[part];
          currentSchema = currentSchema?.properties?.[part];
        }

        // Check if parent is in required array
        const parentKey = pathParts[pathParts.length - 1];
        const parentPath = pathParts.slice(0, -1);
        let parentSchemaContext = schema;
        for (const part of parentPath) {
          parentSchemaContext = parentSchemaContext?.properties?.[part];
        }
        const parentRequired = parentSchemaContext?.required || [];
        const isRequired = parentRequired.includes(parentKey);

        // If it's required, don't filter
        if (isRequired) return false;

        // Check if the object is empty or has no meaningful values
        if (!current || typeof current !== 'object') return true; // null/undefined
        if (Array.isArray(current)) return current.length === 0; // empty array

        // For objects, check if all values are null/undefined/empty
        const values = Object.values(current);
        if (values.length === 0) return true; // empty object
        return values.every(v => v === null || v === undefined || v === '');
      };

      const blockingErrors = Array.isArray(effectiveErrors)
        ? effectiveErrors.filter((error: any) => {
            if (error?.keyword !== "required") return false;

            // Filter out errors for nested fields in optional empty parents
            const instancePath = error.instancePath || '';
            if (instancePath && isOptionalEmptyParent(instancePath, normalizedData, processedSchema)) {
              return false;
            }

            return true;
          })
        : effectiveErrors;

      // Store filtered errors in state so error panel can use them
      setFilteredValidationErrors(Array.isArray(blockingErrors) ? blockingErrors : []);

      const hasJsonFormsErrors =
        blockingErrors &&
        (Array.isArray(blockingErrors)
          ? blockingErrors.length > 0
          : typeof blockingErrors === "object" &&
            Object.keys(blockingErrors).length > 0);

      // If either validation method found errors, prevent submission
      if (hasJsonFormsErrors || missingFields.length > 0) {
        // Extract field names from JSON Forms errors if available
        let errorFields = [...missingFields];

        if (hasJsonFormsErrors && Array.isArray(blockingErrors)) {
          blockingErrors.forEach((error: any) => {
            if (!error || typeof error !== "object") {
              return;
            }

            const path =
              error.instancePath || error.schemaPath || error.path || "";

            // Try to derive a human-friendly field name from the JSON Pointer path,
            // e.g. "#/properties/exchange/properties/platform" -> "Platform"
            const match =
              typeof path === "string"
                ? path.match(/\/properties\/([^/]+)(?:\/properties\/([^/]+))?/)
                : null;

            if (match) {
              const fieldName = match[2] || match[1];
              const displayName =
                fieldName.charAt(0).toUpperCase() +
                fieldName.slice(1).replace(/_/g, " ");
              if (!errorFields.includes(displayName)) {
                errorFields.push(displayName);
              }
              return;
            }

            // Fallback: use the last path segment if available, e.g. "/raw_config" -> "Raw config"
            if (typeof path === "string" && path.startsWith("/")) {
              const segments = path.split("/").filter(Boolean);
              const last = segments[segments.length - 1];
              if (last) {
                const displayName =
                  last.charAt(0).toUpperCase() +
                  last.slice(1).replace(/_/g, " ");
                if (!errorFields.includes(displayName)) {
                  errorFields.push(displayName);
                  return;
                }
              }
            }

            // Last resort: try to extract from error.message or include the message itself
            if (typeof error.message === "string") {
              const msgMatch = error.message.match(
                /required property ['"]([^'"]+)['"]/,
              );
              if (msgMatch && !errorFields.includes(msgMatch[1])) {
                errorFields.push(msgMatch[1]);
              } else if (!errorFields.includes(error.message)) {
                errorFields.push(error.message);
              }
            }
          });
        }

        if (errorFields.length > 0) {
          showError(
            `Please fill in all required fields: ${errorFields.join(", ")}`,
          );
        } else {
          showError(`Please fill in all required fields before submitting.`);
        }
        return;
      }

      setIsSubmitting(true);

      try {
        // As a final safety net, ensure trade_id is present for strategies whose
        // schema defines it, even if previous normalization paths missed it.
        let finalData = normalizedData;
        if (finalData.raw_config && typeof finalData.raw_config === "object") {
          const rc = finalData.raw_config as any;
          const t = rc.trade_id;
          if (t === undefined || t === null || t === "") {
            const rawConfigSchema = processedSchema?.properties?.raw_config;
            const oneOf = Array.isArray(rawConfigSchema?.oneOf)
              ? rawConfigSchema.oneOf
              : null;
            const activeStrategyType = rc.strategy_type;
            const activeBranch =
              oneOf && activeStrategyType
                ? oneOf.find(
                    (b: any) =>
                      b?.properties?.strategy_type?.const ===
                      activeStrategyType,
                  )
                : null;
            const branchHasTradeId =
              activeBranch?.properties &&
              typeof activeBranch.properties === "object" &&
              "trade_id" in activeBranch.properties;

            if (branchHasTradeId) {
              const autoId = generateTradeId(rc.trade_name);
              finalData = {
                ...finalData,
                raw_config: {
                  ...rc,
                  trade_id: autoId,
                },
              };
              onFormDataChange(finalData);
            }
          }
        }

        const body = {
          req_id: uuidv4(),
          connection_id: interaction.session_id,
          algo_id: interaction.algo_id,
          rpc_name: rpcName,
          args: finalData,
        };

        const response = await submitRpc({ data: body }).unwrap();

        // Call success callback if provided
        if (onSubmitConfig.onSuccess) {
          onSubmitConfig.onSuccess(response);
        }

        // Show success message (default: true)
        if (onSubmitConfig.showSuccess !== false) {
          showSuccess(`RPC "${rpcName}" submitted successfully`);
        }

        // Reset form if configured
        if (onSubmitConfig.resetFormOnSuccess) {
          onFormDataChange({});
        }

        // Close modal if configured
        if (onSubmitConfig.closeModal) {
          // This would be handled by parent component
        }
      } catch (error: any) {
        const errorMessage =
          error?.data?.message || error?.message || "Unknown error";

        // Show error message (default: true)
        if (onSubmitConfig.showError !== false) {
          showError(`Failed to submit RPC "${rpcName}": ${errorMessage}`);
        }

        // Call error callback if provided
        if (onSubmitConfig.onError) {
          onSubmitConfig.onError(error);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      interaction.algo_id,
      interaction.session_id,
      rpcName,
      submitRpc,
      onSubmitConfig,
      onFormDataChange,
      showSuccess,
      showError,
      validationErrors,
      processedSchema,
      validateRequiredFields,
      filterValidationErrorsForActiveRawConfig,
    ],
  );

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      className="vertical-tab-panel"
      aria-labelledby={`vertical-tab-${index}`}
    >
      {isActive ? (
        isProcessingSchema ? (
          <div className="pulse-typography-body-1 pulse-color--background-100">
            Loading form...
          </div>
        ) : processedSchema ? (
          // Check if schema has no properties (empty form)
          !processedSchema.properties ||
          Object.keys(processedSchema.properties).length === 0 ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit({});
              }}
              className="vertical-tab-form"
              noValidate
            >
              <div className="vertical-tab-form-content vertical-tab-form-content--empty">
                <div className="vertical-tab-empty-message">
                  <p className="pulse-typography-headline-4 pulse-color--background-200">
                    No arguments required for this request. Press submit below
                    to make the request.
                  </p>
                </div>
              </div>
              <div className="vertical-tab-form-submit">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`pulse-btn--success-700 pulse-color--background-50 pulse-typography-button pulse-disabled-bg-color--background-300`}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          ) : (
            <UseEndpointProvider
              interaction={interaction}
              submitRpc={submitRpc}
              showError={showError}
            >
              <FormContent
                formData={effectiveFormData}
                // Prefer the processed/normalized schema (handles anyOf flattening, object anyOf→oneOf, etc.)
                // and fall back to the raw schema shape if processing failed.
                schemaForForm={
                  processedSchema ?? schemaWithComponents ?? rpcSchema
                }
                onFormDataChange={onFormDataChange}
                onValidationChange={handleValidationChange}
                hasAttemptedSubmit={hasAttemptedSubmit}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                isStartStrategy={!selectedBotData}
                validationErrors={validationErrors}
                filteredValidationErrors={filteredValidationErrors}
                manualValidationErrors={manualValidationErrors}
              />
            </UseEndpointProvider>
          )
        ) : null
      ) : null}
    </div>
  );
};

// Memoized form content component to prevent unnecessary re-renders
const FormContent = React.memo(
  ({
    formData,
    schemaForForm,
    onFormDataChange,
    onValidationChange,
    hasAttemptedSubmit,
    handleSubmit,
    isSubmitting,
    isStartStrategy,
    validationErrors,
    filteredValidationErrors,
    manualValidationErrors,
  }: {
    formData: any;
    schemaForForm: any;
    onFormDataChange: (data: any) => void;
    onValidationChange: (errors: any[]) => void;
    hasAttemptedSubmit: boolean;
    handleSubmit: (data: any) => void;
    isSubmitting: boolean;
    isStartStrategy: boolean;
    validationErrors: any[];
    filteredValidationErrors: any[];
    manualValidationErrors: string[];
  }) => {
    // Memoize form data to prevent creating new object on every render
    const memoizedFormData = useMemo(() => {
      return formData || {};
    }, [formData]);

    // Memoize validation mode
    const validationMode = useMemo(() => {
      return hasAttemptedSubmit ? "ValidateAndShow" : "ValidateAndHide";
    }, [hasAttemptedSubmit]);

    // Memoize form submit handler
    const handleFormSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        handleSubmit(memoizedFormData);
      },
      [handleSubmit, memoizedFormData],
    );

    // Extract human-friendly error messages from validation errors
    const errorMessages = useMemo(() => {
      // Only show errors after first submit attempt
      if (!hasAttemptedSubmit) {
        return [];
      }

      const messages: string[] = [];
      const seen = new Set<string>();

      // Add manual validation errors first (these are simple field names)
      if (manualValidationErrors && manualValidationErrors.length > 0) {
        manualValidationErrors.forEach((fieldName) => {
          messages.push(`${fieldName} is required`);
          seen.add(fieldName.toLowerCase());
        });
      }

      // Use filtered validation errors (excludes errors for optional empty parents)
      const errorsToShow = filteredValidationErrors.length > 0 ? filteredValidationErrors : validationErrors;

      // If no JSON Forms validation errors, return what we have
      if (!errorsToShow || errorsToShow.length === 0) {
        return messages;
      }

      errorsToShow.forEach((error: any) => {
        if (!error) return;

        // Show all validation errors, not just 'required'
        // This includes type errors, enum errors, etc.
        if (error.keyword !== 'required') {
          // Skip non-required errors for now, can be added later if needed
          return;
        }

        const instancePath = error.instancePath || '';
        const missingProperty = error.params?.missingProperty || '';

        // Create human-friendly message
        let message = '';
        if (missingProperty) {
          // Convert snake_case to Title Case
          const readable = missingProperty
            .split('_')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          // Add path context if it's nested
          if (instancePath) {
            const pathParts = instancePath.split('/').filter(Boolean);

            // Remove "config" and "fields" from the path
            const filteredParts = pathParts.filter((part: string) =>
              part !== 'config' && part !== 'fields'
            );

            if (filteredParts.length > 0) {
              const contextPath = filteredParts
                .map((part: string) => {
                  // Handle array indices like quoters/0 -> Quoters #1
                  if (/^\d+$/.test(part)) {
                    return `#${parseInt(part, 10) + 1}`;
                  }
                  return part.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                })
                .join(' → ');

              message = `${contextPath} → ${readable} is required`;
            } else {
              message = `${readable} is required`;
            }
          } else {
            message = `${readable} is required`;
          }
        } else {
          message = error.message || 'Unknown validation error';
        }

        // Avoid duplicates (check both the full key and the field name)
        const key = `${instancePath}:${missingProperty}`;
        const fieldNameLower = missingProperty ? missingProperty.toLowerCase() : '';
        if (!seen.has(key) && !seen.has(fieldNameLower)) {
          seen.add(key);
          seen.add(fieldNameLower);
          messages.push(message);
        }
      });

      return messages;
    }, [hasAttemptedSubmit, validationErrors, filteredValidationErrors, manualValidationErrors]);

    return (
      <form
        onSubmit={handleFormSubmit}
        className="vertical-tab-form"
        noValidate
      >
        {/* Validation Errors Summary */}
        {errorMessages.length > 0 && (
          <div style={{
            backgroundColor: '#fee',
            border: '2px solid #f44',
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '16px',
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#c00',
              marginBottom: '8px',
            }}>
              ⚠️ Please fix the following errors:
            </div>
            <ul style={{
              margin: '0',
              paddingLeft: '24px',
              color: '#800',
            }}>
              {errorMessages.map((msg, idx) => (
                <li key={idx} style={{ marginBottom: '4px' }}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="vertical-tab-form-content">
          <RpcForm
            schema={schemaForForm}
            formData={memoizedFormData}
            onChange={onFormDataChange}
            onValidationChange={onValidationChange}
            validationMode={validationMode}
            populateDefaults={isStartStrategy}
          />
        </div>
        <div className="vertical-tab-form-submit">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`pulse-btn--success-700 pulse-color--background-50 pulse-typography-button pulse-disabled-bg-color--background-300`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    );
  },
);
